<template>
  <div
    class="v2-card"
    :class="[
      `v2-card--${variant}`,
      noPadding ? '' : 'v2-card--padded',
      hoverable ? 'v2-card--hoverable' : '',
    ]"
  >
    <div
      v-if="$slots.header || title"
      class="v2-card__header"
    >
      <slot name="header">
        <h3 class="v2-card__title">
          {{ title }}
        </h3>
        <p
          v-if="subtitle"
          class="v2-card__subtitle"
        >
          {{ subtitle }}
        </p>
      </slot>
    </div>
    <div class="v2-card__body">
      <slot />
    </div>
    <div
      v-if="$slots.footer"
      class="v2-card__footer"
    >
      <slot name="footer" />
    </div>
  </div>
</template>

<script>
export default {
  name: 'V2Card',
  props: {
    title: { type: String, default: '' },
    subtitle: { type: String, default: '' },
    noPadding: { type: Boolean, default: false },
    hoverable: { type: Boolean, default: false },
    // 'panel' = primary surface (.v2-glass-panel — 20px blur, full shadow);
    // 'card'  = nested/secondary surface (.v2-glass-card — 12px blur).
    variant: {
      type: String,
      default: 'panel',
      validator: v => ['panel', 'card'].includes(v),
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/assets/scss/v2/_glass-mixins.scss';

.v2-card {
  overflow: visible;

  // Glass surfaces — light-mode only. Uses the canonical mixins defined
  // in `_glass.scss`, which build the visual from translucent tint +
  // sheen gradient + glass-stroke + compound box-shadow (no per-card
  // `backdrop-filter` — V2Card renders dozens of times per page and the
  // GPU compositing cost would be unsustainable during scroll). Page
  // mesh substrate (LayoutV2::before) provides the optical "frosted"
  // effect once for the whole viewport.
  //
  // Panel = top-level cards, modals, hero containers.
  // Card  = nested or secondary cards within a panel.
  &--panel {
    @include v2-glass-panel-flat;
    border-radius: 1.25rem;
  }

  &--card {
    @include v2-glass-card-flat;
    border-radius: 1rem;
  }

  &--padded {
    .v2-card__header,
    .v2-card__body,
    .v2-card__footer {
      padding: var(--v2-space-4) var(--v2-space-6);
    }
  }

  &--hoverable {
    transition: box-shadow 0.2s ease, transform 0.2s ease, border-color 0.2s ease;
    cursor: pointer;
    &:hover {
      border-color: var(--v2-glass-stroke-strong);
      transform: translateY(-1px);
    }
  }

  &__header {
    border-bottom: 1px solid var(--v2-glass-stroke);
  }

  &__title {
    font-family: var(--v2-font-display);
    font-size: 1.125rem;
    font-weight: 600;
    color: var(--v2-card-foreground);
    margin: 0;
  }

  &__subtitle {
    font-size: 0.875rem;
    color: var(--v2-muted-foreground);
    margin: var(--v2-space-1) 0 0;
  }

  &__footer {
    border-top: 1px solid var(--v2-glass-stroke);
  }
}

@media (prefers-reduced-transparency: reduce) {
  .v2-card {
    &--panel,
    &--card {
      background: var(--v2-card);
      background-image: none;  // suppress the sheen gradient
      box-shadow: var(--v2-shadow-sm);
    }
  }
}
</style>
